import {
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  message,
  notification,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { adminsApi, Admin } from "../../_helpers/apiService";
import { PageTitle } from "../../components";

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

function Admins() {
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [data, setData] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const userData = useSelector((state: any) => state.authentication.userData);

  const fetchAdmins = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const skip = (page - 1) * size;
      const response = await adminsApi.getAll({
        skip,
        limit: size,
      });

      setData(response.data.items);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error("Fetch admins error:", error);
      message.error("Админ хэрэглэгчдийн мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setConfirmLoading(true);
        try {
          await adminsApi.create({
            email: values.email,
            first_name: values.firstName,
            last_name: values.lastName,
            password: values.password || undefined,
          });

          notification.success({
            message: "Амжилттай",
            description: `${values.lastName} ${values.firstName} админ хэрэглэгч амжилттай бүртгэгдлээ.`,
          });

          setVisible(false);
          setConfirmLoading(false);
          form.resetFields();
          fetchAdmins();
        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Create admin error:", error);

          let errorMessage = "Админ хэрэглэгч бүртгэхэд алдаа гарлаа";
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          }

          notification.error({
            message: "Амжилтгүй",
            description: errorMessage,
          });
        }
      })
      .catch((info) => {
        setConfirmLoading(false);
        console.log("Validate Failed:", info);
      });
  };

  const handleActivate = async (admin: Admin) => {
    try {
      await adminsApi.activate(admin.id);
      message.success(`${admin.last_name} ${admin.first_name} идэвхжүүллээ.`);
      fetchAdmins();
    } catch (error: any) {
      console.error("Activate error:", error);
      message.error("Идэвхжүүлэхэд алдаа гарлаа");
    }
  };

  const handleInactivate = async (admin: Admin) => {
    try {
      await adminsApi.inactivate(admin.id);
      message.success(`${admin.last_name} ${admin.first_name} идэвхгүй болголоо.`);
      fetchAdmins();
    } catch (error: any) {
      console.error("Inactivate error:", error);
      message.error("Идэвхгүй болгоход алдаа гарлаа");
    }
  };

  const handleResetPassword = (admin: Admin) => {
    setSelectedAdmin(admin);
    setResetPasswordVisible(true);
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordVisible(false);
    resetPasswordForm.resetFields();
    setSelectedAdmin(null);
  };

  const handleResetPasswordOk = () => {
    resetPasswordForm
      .validateFields()
      .then(async (values) => {
        if (!selectedAdmin) return;

        setResetPasswordLoading(true);
        try {
          await adminsApi.resetPassword(selectedAdmin.id, values.newPassword);

          notification.success({
            message: "Амжилттай",
            description: `${selectedAdmin.last_name} ${selectedAdmin.first_name} нууц үг амжилттай шинэчлэгдлээ.`,
          });

          setResetPasswordVisible(false);
          resetPasswordForm.resetFields();
          setSelectedAdmin(null);
        } catch (error: any) {
          console.error("Reset password error:", error);

          let errorMessage = "Нууц үг шинэчлэхэд алдаа гарлаа";
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          }

          notification.error({
            message: "Амжилтгүй",
            description: errorMessage,
          });
        } finally {
          setResetPasswordLoading(false);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const columns: ColumnsType<Admin> = [
    {
      title: "№",
      key: "index",
      width: 80,
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 150,
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 150,
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 250,
    },
    {
      title: "Төлөв",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (is_active: boolean) => (
        <Tag color={is_active ? "green" : "red"}>
          {is_active ? "Идэвхтэй" : "Идэвхгүй"}
        </Tag>
      ),
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 250,
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleResetPassword(record)}>
            Нууц үг
          </Button>
          {record.is_active ? (
            <Popconfirm
              title="Идэвхгүй болгохдоо итгэлтэй байна уу?"
              onConfirm={() => handleInactivate(record)}
              okText="Тийм"
              cancelText="Үгүй"
            >
              <Button type="link" danger>
                Идэвхгүй болгох
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Идэвхжүүлэхдээ итгэлтэй байна уу?"
              onConfirm={() => handleActivate(record)}
              okText="Тийм"
              cancelText="Үгүй"
            >
              <Button type="link">
                Идэвхжүүлэх
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Only superadmin can access this page
  if (userData.role !== "superadmin") {
    return (
      <>
        <PageTitle title="Админ хэрэглэгчид" description="Хандах эрх байхгүй" />
        <div className="page-content-wrapper">
          <div className="container-fluid">
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>Хандах эрхгүй байна</h3>
              <p>Энэ хуудсанд зөвхөн супер админ хандах боломжтой.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title="Админ хэрэглэгчид"
        description="Админ хэрэглэгчдийн жагсаалт"
        right={
          <Button
            shape="round"
            onClick={() => setVisible(true)}
            icon={<PlusOutlined />}
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            Админ нэмэх
          </Button>
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      rowKey="id"
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} total`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        onChange: (page, size) => {
                          setCurrentPage(page);
                          if (size && size !== pageSize) {
                            setPageSize(size);
                          }
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Админ хэрэглэгч нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={520}
        confirmLoading={confirmLoading}
      >
        <Form
          {...formItemLayout}
          form={form}
        >
          <Form.Item
            label="Имэйл хаяг"
            name="email"
            rules={[
              { required: true, message: "Имэйл хаяг оруулна уу!" },
              { type: "email", message: "Зөв имэйл хаяг оруулна уу!" }
            ]}
          >
            <Input placeholder="admin@example.com" />
          </Form.Item>
          <Form.Item
            label="Овог"
            name="lastName"
            rules={[{ required: true, message: "Овог оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="firstName"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Нууц үг"
            name="password"
            rules={[
              { min: 6, message: "Нууц үг 6-аас дээш тэмдэгт байх ёстой!" }
            ]}
          >
            <Input.Password placeholder="Хоосон үлдээвэл Admin@123" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Нууц үг шинэчлэх - ${selectedAdmin?.last_name} ${selectedAdmin?.first_name}`}
        open={resetPasswordVisible}
        onOk={handleResetPasswordOk}
        onCancel={handleResetPasswordCancel}
        okText="Шинэчлэх"
        cancelText="Болих"
        width={520}
        confirmLoading={resetPasswordLoading}
      >
        <Form
          {...formItemLayout}
          form={resetPasswordForm}
        >
          <Form.Item
            label="Шинэ нууц үг"
            name="newPassword"
            rules={[
              { required: true, message: "Шинэ нууц үг оруулна уу!" },
              { min: 6, message: "Нууц үг 6-аас дээш тэмдэгт байх ёстой!" }
            ]}
          >
            <Input.Password placeholder="Шинэ нууц үг оруулна уу" />
          </Form.Item>
          <Form.Item
            label="Нууц үг давтах"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: "Нууц үг давтаж оруулна уу!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Нууц үг таарахгүй байна!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Нууц үг давтаж оруулна уу" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default Admins;
